import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { ArticleFacadeService } from "../../services/article/article-facade.service";

// UI (comme les autres widgets)
import { SharedModule } from "../../../../shared/shared.module";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

type ViewType = "CHART" | "LIST";

@Component({
  selector: "vl-mouvements-synthese",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PopoverModule,
    WidgetTitleComponent,
    ChipsComponent,
    ChipsToggleComponent,
  ],
  host: { class: "widget-component" },
  styleUrls: ["./mouvements-synthese.component.scss"], // optionnel
  templateUrl: "./mouvements-synthese.component.html",
})
export class MouvementsSyntheseComponent {
  // Inputs usuels
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  // Stats (déjà filtrées en amont par période / articles)
  private articleFacade = inject(ArticleFacadeService);
  private stats = this.articleFacade.getState().stats;

  // UI state
  viewType = signal<ViewType>("CHART");
  elementsView = [
    { icon: "bar_chart", value: "CHART" },
    { icon: "list", value: "LIST" },
  ];

  // Canvas / Chart
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("mv");
  private chart?: Chart;

  // Mapping robustes des types (côté BE: enums)
  private readonly TYPE_RECEPTION = "RECEPTION";
  private readonly TYPE_EXPEDITION = "EXPEDITION";
  private readonly TYPE_CONSO_FAB = "CONSO_FAB";
  private readonly TYPE_FABRICATION = "FABRICATION";
  private readonly TYPE_INTEGRATION = "INTEGRATION_EN_FABRICATION";

  // Lecture mouvements depuis stats
  private mouvements = computed<any[]>(() => {
    const s = this.stats();
    // on tente plusieurs clés possibles, fallback []
    return (s as any)?.mouvements ?? (s as any)?.mouvementsLots ?? [];
  });

  // Agrégations
  private sumByType = (type: string) =>
    this.mouvements()
      .filter((m) => m?.type === type)
      .reduce((a, m) => a + parseNumber(m?.quantite || 0), 0);

  private rec = computed(() => this.sumByType(this.TYPE_RECEPTION));
  private exp = computed(() => this.sumByType(this.TYPE_EXPEDITION));
  private con = computed(() => this.sumByType(this.TYPE_CONSO_FAB));
  private fab = computed(
    () =>
      this.sumByType(this.TYPE_FABRICATION) +
      this.sumByType(this.TYPE_INTEGRATION),
  );

  // Lignes (ordre lisible)
  rows = computed(() => [
    { key: "REC", label: "Réceptions", value: this.rec() },
    {
      key: "FAB",
      label: "Fabrication (incl. intégrations)",
      value: this.fab(),
    },
    { key: "CON", label: "Consommations fabrication", value: this.con() },
    { key: "EXP", label: "Expéditions", value: this.exp() },
  ]);

  display = computed(() => {
    return this.rows()?.some((v) => v.value);
  });

  netTotal = computed(() => this.rec() + this.fab() - this.con() - this.exp());

  // Format
  fmt = (v: number) =>
    new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(v ?? 0);

  // Labels + values pour le chart
  private labels = computed(() => this.rows().map((r) => r.label));
  private values = computed(() => this.rows().map((r) => r.value));

  constructor() {
    // (re)build chart quand data + canvas prêts
    effect(
      () => {
        if (this.canvasRef() && this.mouvements()) {
          this.buildChart();
        }
      },
      { allowSignalWrites: true },
    );

    // MAJ chart live
    effect(
      () => {
        if (!this.chart) return;
        this.chart.data.labels = this.labels() as any;
        (this.chart.data.datasets[0].data as number[]) = this.values();
        this.chart.update("none");
      },
      { allowSignalWrites: true },
    );
  }

  // Même style que "CA par période" (barres pleines on-accent-alt)
  private buildChart() {
    const canvas = this.canvasRef();
    const ctx = canvas?.nativeElement.getContext("2d");
    if (!ctx) return;

    // Détruit l'ancien chart si besoin
    this.chart?.destroy();

    // Couleurs depuis CSS vars (cohérentes avec vos autres widgets)
    const css = getComputedStyle(canvas!.nativeElement);
    const bgAlt = (
      css.getPropertyValue("--color-on-background-alt") || "#476373"
    ).trim();
    const onAccentAlt = (
      css.getPropertyValue("--color-on-accent-alt") || "rgba(197,53,255,.5)"
    ).trim();

    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: this.labels(),
        datasets: [
          {
            label: "Quantité",
            data: this.values(),
            backgroundColor: onAccentAlt,
            borderWidth: 0,
            barThickness: 22,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        scales: {
          x: {
            ticks: { color: bgAlt },
            grid: { color: bgAlt + "55" },
            border: { color: bgAlt + "55" },
          },
          y: {
            ticks: { color: bgAlt },
            grid: { display: false },
            border: { color: bgAlt + "55" },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${this.fmt(ctx.parsed.x)} `,
            },
          },
        },
        layout: { padding: { left: 6, right: 12, top: 6, bottom: 6 } },
        animation: false,
      },
    });
  }
}
